import { pointTest, V4, arrayOf, geq, debug } from '@moleculeone/utils';

function normalizeLengths(longestEdgeLen, otherEdgesLens, minimumRatio) {
  // compress longest edge and stretch the others if necessary, based on minimumRatio
  // for minimumRatio description, look at 'circumscribedPolygon'
  const lenRatio = otherEdgesLens.reduce((a, b) => a + b, 0) / longestEdgeLen;
  if (lenRatio < minimumRatio) {
    const compressionFactor = Math.sqrt(minimumRatio / lenRatio);
    return [longestEdgeLen / compressionFactor, otherEdgesLens.map(e => e * compressionFactor)];
  }
  return [
    longestEdgeLen,
    [...otherEdgesLens], // that way, new array is returned in every case
  ];
}

function circumscribedDiameter(longestEdgeLength, otherEdgesLengths) {
  // what would be the chords' angle formed by all the edges except for the longest one
  // if the circle diameter was of the length of longest edge?
  // if that reference angle > 180*, center of the circle has to be inside the polygon
  // otherwise, it will be outside the polygon.
  // we have to calculate diameter of the circle in which the polygon will be circumscribed in
  // differently for these two cases.
  const referenceAngle = chordAngles(otherEdgesLengths, longestEdgeLength);
  if (referenceAngle > Math.PI) {
    return circumDiamInternal([longestEdgeLength, ...otherEdgesLengths]);
  }
  if (referenceAngle < Math.PI) {
    return circumDiamExternal(longestEdgeLength, otherEdgesLengths);
  }
  return longestEdgeLength;
}

function chordsPath(lenghts, diameter) {
  // list of points that form chords of given lengths, in circle of given diameter
  // we positions it assuming that the circle's center is at (0, 0)
  const path = [V4.vect(diameter / 2.0, 0)];
  for (const length of lenghts) {
    // diameter * 1.000001 to avoid infty values for some cases with overlapping 6-membered rings,
    // where length = diameter
    path.push(V4.rotZ(path[path.length - 1], chordAngle(length, diameter * 1.000001)));
  }
  return path;
}

function chordAngle(length, diameter) {
  const result = 2.0 * Math.asin(length / diameter);
  return Number.isNaN(result) ? Number.POSITIVE_INFINITY : result;
}

function chordAngles(lengths, diameter) {
  let sum = 0.0;
  for (const k of lengths) {
    sum += chordAngle(k, diameter);
  }
  return sum;
}

function circumDiamInternal(edgeLengths) {
  // find the diameter of the circle in which we can circumscribe polygon with given lengths,
  // with circle's center inside polygon.
  // it has to be possible, otherwise NaN will be returned

  // diameter lower bound - if all edges were bent to the shape of circle
  // diameter upper bound - if the polygon is isosceles triangle with vanishing base
  const perimeter = edgeLengths.reduce((a, b) => a + b, 0);
  let minimumDiameter = perimeter / Math.PI;
  let maximumDiameter = perimeter / 2;

  // we're creating chords from the edges of the polygon
  // when chord's angles sum up to 2*PI, they form a polygon that we're looking for
  while (maximumDiameter - minimumDiameter > 0.0001) {
    const midDiameter = (maximumDiameter + minimumDiameter) / 2;
    const midChordAngle = chordAngles(edgeLengths, midDiameter);
    if (midChordAngle > Math.PI * 2) {
      minimumDiameter = midDiameter;
    } else {
      maximumDiameter = midDiameter;
    }
  }
  return (maximumDiameter + minimumDiameter) / 2;
}

function circumDiamExternal(longestEdgeLength, otherEdgeLengths) {
  // find the diameter of the circle in which we can circumscribe polygon with given lengths,
  // with circle's center outside polygon.
  // it has to be possible, otherwise - ???
  // (probably longestEdgeLength will be returned or search won't complete)

  // diameter (result) is at least the length of the longest edge,
  // but we don't have any upper bound candidate - we need to find it
  let minimumDiameter = longestEdgeLength;
  let maximumDiameter = longestEdgeLength * 2;
  // at minimumDiameter, longest edge forms chord of bigger angle than the sum of all other angles
  // we're looking for maximumDiameter that has the opposite property
  // result will be something in between.
  while (chordAngle(longestEdgeLength, maximumDiameter) > chordAngles(otherEdgeLengths, maximumDiameter)) {
    minimumDiameter *= 2;
    maximumDiameter *= 2;
  }

  // find diameter that satisfies 'chord_angle(longestEdge) == sum_i(chord_angle(other_edge_i))'
  while (maximumDiameter - minimumDiameter > 0.0001) {
    const midDiameter = (maximumDiameter + minimumDiameter) / 2;
    const longestEdgeAngle = chordAngle(longestEdgeLength, midDiameter);
    const otherEdgesAngle = chordAngles(otherEdgeLengths, midDiameter);
    if (longestEdgeAngle > otherEdgesAngle) {
      minimumDiameter = midDiameter;
    } else {
      maximumDiameter = midDiameter;
    }
  }
  return (maximumDiameter + minimumDiameter) / 2;
}

export function circumscribedPolygon(edgeLengths, minimumRatio = 1.4) {
  // minimumRatio = minimum value of (sum of lengths of shorter edges)/(length of the longest edge)
  // if the ratio is lower than that, longest edge is compressed and other edges are stretched
  // it's done to avoid returning very flat polygons
  // minimumRatio has to be greater or equal 1, otherwise, it would be impossible to form a polygon:
  // longest edge cannot be longer than sum of all the other edges
  debug(arrayOf(geq(0)), edgeLengths);
  debug(geq(1), minimumRatio);

  // separate longest edge and other edges
  let longestEdge = Math.max(...edgeLengths);
  const longestEdgeIdx = edgeLengths.indexOf(longestEdge);
  let otherEdges = [...edgeLengths];
  otherEdges.splice(longestEdgeIdx, 1);

  [longestEdge, otherEdges] = normalizeLengths(longestEdge, otherEdges, minimumRatio);
  const diameter = circumscribedDiameter(longestEdge, otherEdges);

  // put back new edge and return path of polygon circumscribed in a circle of given diameter
  const normalizedLengths = [...otherEdges];
  normalizedLengths.splice(longestEdgeIdx, 0, longestEdge);
  const polygonPath = chordsPath(normalizedLengths, diameter);
  polygonPath.pop(); // last point is the same as the first one, as path forms a polygon
  return debug(arrayOf(pointTest), polygonPath);
}
